import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Cpu, 
  Zap, 
  Shield, 
  Globe, 
  TrendingDown, 
  Truck,
  Users,
  Car,
  Factory,
  Radio,
  Smartphone,
  Activity,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const Home = () => {
  const productCategories = [
    { 
      icon: Cpu, 
      title: 'Semiconductors (ICs)', 
      description: 'Microcontrollers, Memory ICs, Power Management, Analog & Logic ICs'
    },
    { 
      icon: Zap, 
      title: 'Discrete Components', 
      description: 'Diodes, Transistors, MOSFETs, IGBTs, Thyristors'
    },
    { 
      icon: Activity, 
      title: 'Passive Components', 
      description: 'Resistors, Capacitors, Inductors, Transformers'
    },
    { 
      icon: Radio, 
      title: 'Active Components', 
      description: 'Sensors, Relays, Optocouplers, Modules'
    },
    { 
      icon: Users, 
      title: 'Electromechanical Components', 
      description: 'Connectors, Switches, Relays, Terminal Blocks'
    }
  ];

  const whyChooseUs = [
    { icon: Shield, title: '100% Genuine Components', description: 'Full traceability and quality assurance' },
    { icon: Globe, title: 'Global Sourcing Network', description: 'Access to worldwide component suppliers' },
    { icon: TrendingDown, title: 'Competitive Pricing', description: 'Best rates for bulk and regular orders' },
    { icon: Truck, title: 'Fast Delivery', description: 'Quick turnaround time for urgent requirements' },
    { icon: Users, title: 'Strong Vendor Network', description: 'Partnerships with leading manufacturers' },
    { icon: CheckCircle2, title: 'Expert Technical Support', description: 'Professional assistance for component selection' }
  ];

  const industries = [
    { icon: Car, name: 'Automotive' },
    { icon: Factory, name: 'Industrial' },
    { icon: Radio, name: 'Telecom' },
    { icon: Smartphone, name: 'Consumer Electronics' },
    { icon: Activity, name: 'Medical Electronics' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-title">
              Your Trusted Global Partner for Genuine Electronic Components
            </h1>
            <p className="hero-subtitle">
              Specialized in Semiconductors, ICs, Active & Passive Components
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/contact">
                <Button size="lg" className="cta-primary">
                  Request a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="cta-secondary">
                  Send BOM
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Electronics Banner */}
      <section className="electronics-banner">
        <div className="container mx-auto px-6 py-20">
          <div className="banner-content">
            <div className="banner-text">
              <h2 className="banner-title">Precision. Quality. Reliability.</h2>
              <p className="banner-description">
                Leading supplier of genuine electronic components with global sourcing capabilities
              </p>
            </div>
            <div className="banner-image-wrapper">
              <img 
                src="https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?auto=format&fit=crop&w=1200&q=80" 
                alt="Electronics Components" 
                className="banner-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-title">About Shree Techno Products</h2>
            <p className="section-description">
              Shree Techno Products is a leading distributor of electronic components, specializing in 
              semiconductors and integrated circuits. We serve OEMs, EMS companies, manufacturers, and 
              R&D organizations with genuine components sourced from authorized suppliers worldwide. 
              Our commitment to quality, competitive pricing, and exceptional service makes us the 
              preferred choice for electronic component procurement.
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="products-section">
        <div className="container mx-auto px-6 py-16">
          <h2 className="section-title text-center mb-12">Product Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {productCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="product-card">
                  <CardContent className="p-6">
                    <div className="icon-wrapper mb-4">
                      <IconComponent className="h-10 w-10" />
                    </div>
                    <h3 className="card-title mb-2">{category.title}</h3>
                    <p className="card-description">{category.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/products">
              <Button variant="outline" size="lg">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section">
        <div className="container mx-auto px-6 py-16">
          <h2 className="section-title text-center mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="feature-card">
                  <div className="icon-wrapper-sm mb-4">
                    <IconComponent className="h-8 w-8" />
                  </div>
                  <h3 className="feature-title">{item.title}</h3>
                  <p className="feature-description">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="industries-section">
        <div className="container mx-auto px-6 py-16">
          <h2 className="section-title text-center mb-12">Industries We Serve</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <div key={index} className="industry-card">
                  <IconComponent className="h-12 w-12 mb-3" />
                  <p className="industry-name">{industry.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container mx-auto px-6 py-16">
          <div className="cta-box">
            <h2 className="cta-title">Send Your BOM for Immediate Quote</h2>
            <p className="cta-description">
              Upload your Bill of Materials and receive a competitive quote within 24 hours
            </p>
            <Link to="/contact">
              <Button size="lg" className="cta-button">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
